/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';

import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';

import ColorWrapper from '@styles/colors/index';
import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import Svg from '@styles/icons/svg';

import Header from '@components/settings/v2/common/SettingsHeader';
import { BACK_BUTTON_SETTINGS_TEXT, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';

import { useManageSupport } from './hooks/useManageSupport';

const { supportScreen } = TEST_IDS;

const VersionLine = ({ C, version, name }) => {
  return (
    <View
      style={[
        {
          width: '100%',
          gap: scale(10),
          flexDirection: 'row',
        },
      ]}
    >
      <View style={{ flex: 1 }}>
        <Text
          style={[
            {
              color: C('Label / Light / Tertiary'),
              textAlign: 'right',
            },
            font.Body,
          ]}
        >
          {i18n.t(name)}:
        </Text>
      </View>
      <View style={{ flex: 1 }}>
        <Text
          style={[
            {
              color: C('Label / Light / Tertiary'),
            },
            font.Body,
          ]}
        >
          {version}
        </Text>
      </View>
    </View>
  );
};

const Support = ({ C }) => {
  const {
    backend,
    askForHelpLine,
    feedbackItems,
    goToSecret,
    appVersion,
    backendVersion,
    minConnectorVersion,
    actualConnectorVersion,
  } = useManageSupport();

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return [
    <View key="Support" style={{ width: '100%', height: '100%', backgroundColor: C('Background/Light/Detail'), alignItems: 'center' }}>
      <Header backBtnText={BACK_BUTTON_SETTINGS_TEXT} name={'Support'} key={'CompanyHeader'} />
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View
          style={{
            paddingHorizontal: scale(18),
          }}
        >
          <View testID={supportScreen.askForHelpSection}>
            <View style={{ paddingLeft: scale(15), marginBottom: scale(5) }}>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('ASK FOR HELP')}</Text>
            </View>
            <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              {askForHelpLine.map((item, index) => (
                <Line
                  {...{
                    item,
                    index,
                    array: askForHelpLine,
                    C,
                    roundBottom: askForHelpLine.length - 1 === index,
                    roundTop: index === 0,
                    topBorder: index !== 0,
                  }}
                  key={item.name}
                />
              ))}
            </View>
          </View>
          <View style={{ marginTop: scale(38) }} testID={supportScreen.feedbackSection}>
            <View style={{ paddingLeft: scale(15), marginBottom: scale(5) }}>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('PROVIDE FEEDBACK')}</Text>
            </View>
            <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              {feedbackItems.map((item, index) => (
                <Line
                  {...{
                    item,
                    index,
                    array: feedbackItems,
                    C,
                    roundBottom: feedbackItems.length - 1 === index,
                    roundTop: index === 0,
                    topBorder: index !== 0,
                    bigSize: true,
                  }}
                  key={item.name}
                />
              ))}
            </View>
          </View>
        </View>
        <TouchableElement onPress={goToSecret}>
          {({}) => (
            <View style={{ justifyContent: 'center' }}>
              <View style={{ marginTop: scale(64), alignItems: 'center' }}>
                <Svg icon="SmartFactoryFill" color="Label/Light/Tertiary" width={scale(145)} height={scale(70)} />
              </View>
              <View style={{ marginTop: scale(30), gap: scale(4) }} testID={supportScreen.versionSection}>
                <Text
                  style={[
                    {
                      color: C('Label / Light / Tertiary'),
                      textAlign: 'center',
                    },
                    font.Headline,
                  ]}
                >
                  {i18n.t('Version')}
                </Text>
                <VersionLine {...{ C, version: appVersion, name: 'App' }} />
                <VersionLine {...{ C, version: backendVersion, name: 'Backend' }} />
                <VersionLine {...{ C, version: minConnectorVersion, name: 'Minimum Connector Version' }} />
                <VersionLine {...{ C, version: actualConnectorVersion, name: 'Actual Connector Version' }} />
              </View>
              <View style={{ marginTop: scale(30), gap: scale(4) }} testID={supportScreen.environmentSection}>
                <Text
                  style={[
                    {
                      color: C('Label / Light / Tertiary'),
                      textAlign: 'center',
                    },
                    font.Headline,
                  ]}
                >
                  {i18n.t('Environment')}
                </Text>
                {backend && <VersionLine {...{ C, version: backend, name: 'Backend' }} />}
              </View>
            </View>
          )}
        </TouchableElement>
      </ScrollView>
    </View>,
  ];
};

export default ColorWrapper(Support);
